import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { getCategoriesFromAPI } from '@/services/products.service';

/* ============================================================
   LOGOS - CUANDO SUBAS TUS IMAGENES A src/assets/:
   1. Pon tu logo AZUL en: src/assets/onfase-logo.png
   2. Pon tu logo BLANCO/CELESTE en: src/assets/onfase-logo-white.png
   3. Descomenta las 2 lineas de import de abajo
   4. Comenta las 2 URLs de abajo
   ============================================================ */
  import logoImg from '@/assets/onfase-logo.png';
  import logoWhiteImg from '@/assets/onfase-logo-white.png';

// Logo azul (navbar blanco / scrolled / otras paginas)
//const logoImg = 'https://storage.readdy-site.link/project_files/4f188a96-057d-4221-af00-461f9392b074/2162d8b8-0897-4890-9dca-b50267086c94_ONFASE-cmyk.png?v=4d1d5149037f2c8de4b1870ef1b084c1';

// Logo blanco+celeste (hero oscuro / sin scroll / mobile home)
//const logoWhiteImg = 'https://public.readdy.ai/ai/img_res/d7d2bf32-154a-42c8-b0c2-deac0307a501.png';

interface NavCategory { id: string; name: string; icon: string; count: number; }
interface NavItem { label: string; href: string; isScroll?: boolean; scrollId?: string; }

const NAV_ITEMS: NavItem[] = [
  { label: 'Inicio',    href: '/' },
  { label: 'Productos', href: '/products' },
  { label: 'Servicios', href: '/#servicios', isScroll: true, scrollId: 'servicios' },
  { label: 'Nosotros',  href: '/about' },
  { label: 'Contacto',  href: '/contact' },
];

const CATEGORY_ICONS: Record<string, string> = {
  solar: 'ri-sun-line', 'materia-prima': 'ri-box-3-line', herramientas: 'ri-hammer-line',
  baterias: 'ri-battery-charge-line', inversores: 'ri-flashlight-line', cables: 'ri-plug-line',
  accesorios: 'ri-settings-3-line', iluminacion: 'ri-lightbulb-line', electrodomesticos: 'ri-tv-line',
  climatizacion: 'ri-temp-hot-line', seguridad: 'ri-shield-line', redes: 'ri-wifi-line',
  kits: 'ri-tools-line', paneles: 'ri-sun-line',
};

function getCategoryIcon(slug: string): string {
  const key = Object.keys(CATEGORY_ICONS).find((k) => slug.includes(k) || k.includes(slug));
  return key ? CATEGORY_ICONS[key] : 'ri-grid-line';
}

function scrollToSection(id: string, retries = 8) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } else if (retries > 0) {
    setTimeout(() => scrollToSection(id, retries - 1), 250);
  }
}

function NavLink({ item, activeHref, isHomeAndNotScrolled }: { item: NavItem; activeHref: string; isHomeAndNotScrolled: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = activeHref === item.href;

  const handleClick = (e: React.MouseEvent) => {
    if (!item.isScroll) return;
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate(`/?section=${item.scrollId}`);
    } else {
      window.history.replaceState(null, '', `#${item.scrollId}`);
      scrollToSection(item.scrollId!);
    }
  };

  const baseColor = isHomeAndNotScrolled
    ? 'text-white/90 hover:text-white'
    : 'text-stone-600 hover:text-[#1e2d4a]';

  return (
    <Link
      to={item.isScroll ? `/?section=${item.scrollId}` : item.href}
      onClick={handleClick}
      aria-current={isActive ? 'page' : undefined}
      className={`nav-link font-medium text-base transition-colors cursor-pointer whitespace-nowrap focus-visible:outline-2 focus-visible:outline-offset-2 rounded-sm ${isActive ? 'text-[#4bbfdf]' : baseColor}`}
    >
      {item.label}
    </Link>
  );
}

function MobileNavItem({ item, onClose, activeHref }: { item: NavItem; onClose: () => void; activeHref: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = activeHref === item.href;

  const handleClick = (e: React.MouseEvent) => {
    if (!item.isScroll) {
      onClose();
      return;
    }
    e.preventDefault();
    onClose();
    if (location.pathname !== '/') {
      navigate(`/?section=${item.scrollId}`);
    } else {
      window.history.replaceState(null, '', `#${item.scrollId}`);
      setTimeout(() => scrollToSection(item.scrollId!), 150);
    }
  };

  return (
    <Link
      to={item.isScroll ? `/?section=${item.scrollId}` : item.href}
      onClick={handleClick}
      aria-current={isActive ? 'page' : undefined}
      className={`flex items-center justify-between py-3.5 px-4 rounded-xl text-sm font-medium transition-colors cursor-pointer min-h-[48px] ${isActive ? 'text-[#4bbfdf]' : 'text-stone-700 hover:text-stone-900'}`}
      style={isActive ? { background: 'rgba(75,191,223,0.10)' } : {}}
    >
      {item.label}
      {isActive && <i className="ri-check-line text-[#4bbfdf] text-base" aria-hidden="true" />}
    </Link>
  );
}

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileCategories, setMobileCategories] = useState<NavCategory[]>([]);
  const [scrolled, setScrolled] = useState(false);
  const { cart, openCart } = useCart();
  const cartCount = cart.itemCount;
  const location = useLocation();
  const firstMenuItemRef = useRef<HTMLAnchorElement>(null);
  const isHome = location.pathname === '/';

  const activeHref = (() => {
    if (location.pathname !== '/') {
      return location.pathname;
    }
    const hash = location.hash.replace('#', '');
    const scrollItem = NAV_ITEMS.find((i) => i.isScroll && i.scrollId === hash);
    if (scrollItem) return scrollItem.href;
    return '/';
  })();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    getCategoriesFromAPI()
      .then((cats) => setMobileCategories(cats.filter((c) => c.count > 0).map((c) => ({ id: c.id, name: c.name, icon: getCategoryIcon(c.id), count: c.count }))))
      .catch(() => {});
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    if (menuOpen) setTimeout(() => firstMenuItemRef.current?.focus(), 100);
  }, [menuOpen]);

  const showWhiteLogo = isHome && !scrolled;

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:bg-white focus:text-primary-700 focus:font-semibold focus:px-4 focus:py-2 focus:rounded-lg focus:outline-2 focus:outline-primary-500"
      >
        Saltar al contenido
      </a>

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
          scrolled || !isHome
            ? 'backdrop-blur-md border-b border-neutral-200/80'
            : 'bg-transparent border-b border-transparent'
        }`}
        style={scrolled || !isHome ? { background: 'rgba(255,255,255,0.98)', boxShadow: '0 1px 12px rgb(0 0 0 / 0.05)' } : {}}
        role="banner"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="h-16 flex items-center justify-between gap-4 md:gap-6 relative">

            <Link to="/" aria-label="ONFASE — Ir al inicio" className="flex items-center shrink-0 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 rounded-sm relative">
              {/* Logo blanco - siempre cargado, opacidad controla visibilidad */}
              <img
                src={logoWhiteImg}
                alt="ONFASE"
                className={`w-auto object-contain transition-all duration-500 ease-out absolute top-0 left-0 ${showWhiteLogo ? 'h-14 md:h-24' : 'h-5 md:h-8'}`}
                style={{ opacity: showWhiteLogo ? 1 : 0, pointerEvents: showWhiteLogo ? 'auto' : 'none' }}
                width="160"
              />
              {/* Logo azul - siempre cargado, opacidad controla visibilidad */}
              <img
                src={logoImg}
                alt="ONFASE"
                className={`w-auto object-contain transition-all duration-500 ease-out ${showWhiteLogo ? 'h-14 md:h-24' : 'h-5 md:h-8'}`}
                style={{ opacity: showWhiteLogo ? 0 : 1 }}
                width="160"
              />
            </Link>

            <nav aria-label="Navegación principal" className="hidden md:flex absolute left-1/2 -translate-x-1/2 items-center gap-4 lg:gap-6">
              {NAV_ITEMS.map((item) => (
                <NavLink key={item.href} item={item} activeHref={activeHref} isHomeAndNotScrolled={isHome && !scrolled} />
              ))}
            </nav>

            <div className="hidden md:flex items-center gap-2">
              <button
                onClick={openCart}
                aria-label={cartCount > 0 ? `Carrito — ${cartCount} ${cartCount === 1 ? 'producto' : 'productos'}` : 'Abrir carrito'}
                className={`relative w-10 h-10 flex items-center justify-center rounded-xl transition-colors duration-300 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 ${isHome && !scrolled ? 'text-white/90 hover:text-white' : 'text-stone-500 hover:text-[#1e2d4a]'}`}
              >
                <i className="ri-shopping-cart-line text-xl" aria-hidden="true" />
                {cartCount > 0 && (
                  <span aria-hidden="true" className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-white text-[10px] font-bold rounded-full" style={{ background: '#1e2d4a' }}>
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </button>
              <Link
                to="/products"
                className="inline-flex items-center gap-1.5 text-white text-sm font-bold px-4 py-2 rounded-lg transition-colors duration-300 cursor-pointer whitespace-nowrap focus-visible:outline-2 focus-visible:outline-offset-2"
                style={{ background: '#4bbfdf' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#2aa8cc'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = '#4bbfdf'; }}
              >
                <i className="ri-store-2-line text-base" aria-hidden="true" />
                Ver catálogo
              </Link>
            </div>

            <div className="md:hidden flex items-center gap-1">
              <button
                onClick={openCart}
                aria-label={cartCount > 0 ? `Carrito — ${cartCount} productos` : 'Abrir carrito'}
                className={`relative w-11 h-11 flex items-center justify-center rounded-xl transition-colors duration-300 cursor-pointer overflow-visible ${isHome && !scrolled ? 'text-white' : 'text-stone-600'}`}
              >
                <i className="ri-shopping-cart-line text-xl" aria-hidden="true" />
                {cartCount > 0 && (
                  <span aria-hidden="true" className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center text-white text-[9px] font-bold rounded-full" style={{ background: '#1e2d4a' }}>
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú de navegación'}
                aria-expanded={menuOpen}
                aria-controls="mobile-menu"
                className={`w-11 h-11 flex items-center justify-center rounded-xl transition-colors duration-300 cursor-pointer ${isHome && !scrolled ? 'text-white hover:bg-white/10' : 'text-stone-600 hover:bg-stone-100'}`}
              >
                <i className={`text-xl transition-all duration-200 ${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div
        className={`fixed inset-0 bg-black/40 backdrop-blur-sm z-40 md:hidden transition-opacity duration-300 ${menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />

      <div
        id="mobile-menu"
        role="dialog"
        aria-modal="true"
        aria-label="Menú de navegación"
        className={`fixed top-0 right-0 h-full w-72 max-w-[85vw] z-50 md:hidden flex flex-col transition-transform duration-300 ease-out ${menuOpen ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ background: '#ffffff', boxShadow: '-4px 0 30px rgb(0 0 0 / 0.12)' }}
      >
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid #e5e7eb' }}>
          <img
            src={logoImg}
            alt="ONFASE"
            className="h-7 w-auto object-contain"
            width="130" height="32"
          />
          <button
            onClick={() => setMenuOpen(false)}
            aria-label="Cerrar menú"
            className="w-10 h-10 flex items-center justify-center rounded-xl text-stone-400 hover:text-stone-800 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl" aria-hidden="true" />
          </button>
        </div>

        <nav aria-label="Menú móvil" className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
          {NAV_ITEMS.map((item) => (
            <MobileNavItem
              key={item.href}
              item={item}
              onClose={() => setMenuOpen(false)}
              activeHref={activeHref}
            />
          ))}
        </nav>

        <div className="px-4 py-5" style={{ borderTop: '1px solid #e5e7eb' }}>
          <Link
            to="/products"
            onClick={() => setMenuOpen(false)}
            className="w-full flex items-center justify-center gap-2 text-white text-sm font-bold py-3.5 rounded-xl cursor-pointer whitespace-nowrap transition-colors min-h-[48px]"
            style={{ background: '#4bbfdf' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#2aa8cc'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = '#4bbfdf'; }}
          >
            <i className="ri-store-2-line" aria-hidden="true" />
            Ver catálogo completo
          </Link>
        </div>
      </div>
    </>
  );
}